import uid from "common/utils/uid";
import type { DocMigration } from "../docMigration";

// eslint-disable-next-line @typescript-eslint/naming-convention
const migration_0_0_292: DocMigration = {
  maxSchemaVersion: "0.0.292",
  description: "Electrical System Flatten",
  apply(json: any): any {
    const {
      loads,
      controllers,
      embeddedGroups,
      roomEmbeddedGroups,
      independentLoadArrays,
      roomIndependentLoadArrays,
      roomClearRules,
    } = json.electricalSystem;

    // clear locked entities that are no longer valid
    for (const lockedId in json.locked) {
      const entity = json.locked[lockedId];
      if (
        entity.type === "ELECTRICAL_ROOM_GROUP" ||
        entity.type === "ELECTRICAL_HOME_GROUP" ||
        entity.type === "ELECTRICAL_CONTROLLER"
      ) {
        delete json.locked[lockedId];
      }
    }

    for (const settingId in json.settings) {
      const setting = json.settings[settingId];
      if (setting.type !== "ELECTRICAL_SETTINGS") {
        continue;
      }

      for (const ruleId in setting.rules) {
        const rule = setting.rules[ruleId];
        if (rule.electricalLayout.type !== undefined) {
          rule.electricalLayout.layoutType = rule.electricalLayout.type;
          delete rule.electricalLayout.type;
        }
        setting.rules[ruleId] = rule;
      }

      json.settings[settingId] = setting;
    }

    let maxGroupSortIndex = Object.values(json.groups || {}).reduce(
      (a, b) => Math.max(a as any, (b as any).sortIndex ?? a),
      0,
    ) as number;

    const tempElecGroupsFinal = [] as any[];
    const tempElecGroupsWithHomeLoads = [] as any[];
    const tempInplaceLayouts = {};

    let layoutSortIndex = 0;

    for (const roomId in roomEmbeddedGroups) {
      const roomAssignment = roomEmbeddedGroups[roomId];
      for (const roomGroupId in roomAssignment) {
        const roomGroup = embeddedGroups[roomGroupId];
        const { name, loadArrays, controllers: controllerIds, inPlaceLayouts, disabled } = roomGroup;

        for (const inplaceLayoutId in inPlaceLayouts) {
          const inPlaceLayout = convertInPlaceLayout(
            inPlaceLayouts[inplaceLayoutId],
            disabled,
            layoutSortIndex,
            roomId,
          );
          layoutSortIndex++;
          tempInplaceLayouts[inPlaceLayout.id] = inPlaceLayout;
        }

        // used to deduplicate electrical with home groups and put them in proper order
        const tempElecGroup = {
          homeLoadIds: {},
          name,
          disabled: undefined,
          loads: [] as any[],
          controllers: [] as any[],
        };

        //loads in roomgroup and homegroups
        for (const loadArray of loadArrays) {
          switch (loadArray.type) {
            case "LOAD": {
              const load = loads[loadArray.id];
              if (!load) {
                continue;
              }
              const electricalDevice = convertLoad(load, roomId, disabled, false);
              tempElecGroup.loads.push(electricalDevice);

              break;
            }
            case "INDEPENDENT_BY_NAME_KEY": {
              const homeGroup = independentLoadArrays[loadArray.independentArrayRefId];
              if (!homeGroup) {
                continue;
              }

              if (!tempElecGroup.homeLoadIds[homeGroup.id]) {
                tempElecGroup.homeLoadIds[homeGroup.id] = true;
              }

              //find roomId for this loadArray
              let parentId: string | undefined = undefined;
              for (const loadArrayRoomId in roomIndependentLoadArrays) {
                const loadArrayRoomAssignment = roomIndependentLoadArrays[loadArrayRoomId];
                if (loadArrayRoomAssignment[homeGroup.id]) {
                  parentId = loadArrayRoomId;
                  break;
                }
              }

              for (const loadId of homeGroup.loads) {
                const load = loads[loadId];
                if (!load) {
                  continue;
                }
                const electricalDevice = convertLoad(load, parentId, disabled, true);
                tempElecGroup.loads.push(electricalDevice);
              }

              break;
            }
          }
        }

        for (const controllerId of controllerIds) {
          const controller = controllers[controllerId];
          if (!controller) {
            continue;
          }
          const electricalDevice = convertController(controller, roomId, disabled);
          tempElecGroup.controllers.push(electricalDevice);
        }

        if (!Object.keys(tempElecGroup.homeLoadIds).length) {
          // since this is a simple group (sans home group), we can add the disabled from the room group
          // but first we need to strip the disabled from it's loads and controllers
          for (const load of tempElecGroup.loads) {
            delete load.disabled;
          }
          for (const controller of tempElecGroup.controllers) {
            delete controller.disabled;
          }

          tempElecGroupsFinal.push({ ...tempElecGroup, disabled });
          continue;
        }

        // enables joining multiple groups with home loads into one group
        let addToExistingGroup = false;
        let index: number | undefined = undefined;

        for (const listIndex of Object.keys(tempElecGroupsWithHomeLoads)) {
          const list = tempElecGroupsWithHomeLoads[listIndex];
          for (const innerGroup of list) {
            const { homeLoadIds } = innerGroup;

            for (const key in tempElecGroup.homeLoadIds) {
              if (homeLoadIds[key]) {
                addToExistingGroup = true;
                index = +listIndex;
                break;
              }
            }
            if (addToExistingGroup) {
              break;
            }
          }
        }

        if (addToExistingGroup) {
          const list = tempElecGroupsWithHomeLoads[index as number];
          list.push(tempElecGroup);
          tempElecGroupsWithHomeLoads[index as number] = list;
          continue;
        }

        tempElecGroupsWithHomeLoads.push([tempElecGroup]);
      }
    }

    // pass groups with one
    for (const groups of tempElecGroupsWithHomeLoads) {
      if (groups.length === 1) {
        const elecGroup = groups[0];
        // since this is a simple group (sans home group), we can add the disabled from the room group
        tempElecGroupsFinal.push(elecGroup);
      } else {
        const mergedGroup = mergeElectricalGroups(groups.sort((a, b) => b.loads.length - a.loads.length));
        if (mergedGroup) {
          tempElecGroupsFinal.push(mergedGroup);
        }
      }
    }

    const electricalDevices = {};

    // populate the electrical devices and groups
    let deviceSortIndex = 0;
    for (const group of tempElecGroupsFinal) {
      const { name, disabled, loads, controllers } = group;
      maxGroupSortIndex++;

      const groupId = uid();

      for (const load of loads) {
        const loadWithSort = { ...load, sortIndex: deviceSortIndex };
        electricalDevices[loadWithSort.id] = loadWithSort;
        json.entityGroups[`electricalDevice.${loadWithSort.id}`] = groupId;
        deviceSortIndex++;
      }

      for (const controller of controllers) {
        const controllerWithSort = { ...controller, sortIndex: deviceSortIndex };
        json.entityGroups[`electricalDevice.${controllerWithSort.id}`] = groupId;
        electricalDevices[controllerWithSort.id] = controllerWithSort;
        deviceSortIndex++;
      }

      json.groups[groupId] = {
        entityType: "GROUP",
        id: groupId,
        disabled,
        sortIndex: maxGroupSortIndex,
        name,
      };
    }

    for (const roomId in roomClearRules) {
      (json.rooms[roomId] as any).suppressElectricalFromSettings = true;
    }

    json.electricalDevices = electricalDevices;
    json.electricalLayouts = tempInplaceLayouts;

    delete (json as any).electricalSystem;

    return json;
  },
};

export default migration_0_0_292;

/**
 * Merges multiple electrical groups into one group. Home groups can be used to link room groups together.
 */
function mergeElectricalGroups(groups) {
  const newLoads: any[] = [];
  const newControllers: any[] = [];

  const loadAlreadyAddedToIndex: { [uid: string]: boolean } = {};
  const controllerAlreadyAddedToIndex: { [uid: string]: boolean } = {};

  for (const group of groups) {
    const { controllers, loads } = group;

    for (const load of loads) {
      if (!loadAlreadyAddedToIndex[load.id]) {
        loadAlreadyAddedToIndex[load.id] = true;
        newLoads.push(load);
      }
    }

    for (const controller of controllers) {
      if (!controllerAlreadyAddedToIndex[controller.id]) {
        controllerAlreadyAddedToIndex[controller.id] = true;
        newControllers.push(controller);
      }
    }
  }

  return { controllers: newControllers, loads: newLoads, name: groups[0].name, disabled: undefined };
}

function convertLoad(l: any, parentId: string | undefined, disabled: boolean, fromHomeGroup: boolean) {
  if (fromHomeGroup) {
    return {
      entityType: "ELECTRICAL_DEVICE",
      deviceType: "LOAD",
      id: l.id,
      sortIndex: undefined, // will be set later
      parentId,
      disabled,
      name: l.name,
      loadType: l.loadType,
      position: { ...l.position },
      hideConnections: l.hideConnections,
      fromlegacyHomeGroup: true,
    };
  }

  return {
    entityType: "ELECTRICAL_DEVICE",
    deviceType: "LOAD",
    id: l.id,
    disabled,
    sortIndex: undefined, // will be set later
    parentId,
    name: l.name,
    loadType: l.loadType,
    position: { ...l.position },
    hideConnections: l.hideConnections,
  };
}

function convertController(c: any, parentId: string, disabled: boolean) {
  switch (c.controllerType) {
    case "SENSOR": {
      return {
        entityType: "ELECTRICAL_DEVICE",
        deviceType: "CONTROLLER",
        id: c.id,
        disabled,
        name: c.name,
        controllerType: c.controllerType,
        sortIndex: undefined, // will be set later
        parentId,
        selections: c.selections,
        connection: c.connection,
      };
    }
    case "OPENER": {
      return {
        entityType: "ELECTRICAL_DEVICE",
        deviceType: "CONTROLLER",
        id: c.id,
        disabled,
        name: c.name,
        controllerType: c.controllerType,
        sortIndex: undefined, // will be set later
        parentId,
        selections: c.selections,
        connection: c.connection,
      };
    }
    case "SWITCH": {
      return {
        entityType: "ELECTRICAL_DEVICE",
        deviceType: "CONTROLLER",
        id: c.id,
        name: c.name,
        disabled,
        controllerType: c.controllerType,
        sortIndex: undefined, // will be set later
        parentId,
        selections: c.selections,
        switchType: c.switchType,
        connection: c.connection,
      };
    }
  }
  return undefined;
}

function convertInPlaceLayout(l: any, disabled: boolean, sortIndex: number, parentId: string) {
  const layoutWithoutId = { ...l };
  layoutWithoutId.layoutType = layoutWithoutId.type;
  delete layoutWithoutId.id;
  delete layoutWithoutId.type;

  return {
    entityType: "ELECTRICAL_LAYOUT",
    id: uid(),
    disabled,
    parentId,
    name: l.name,
    sortIndex, // will be set later
    layout: layoutWithoutId,
  };
}
